import React, { useEffect, useReducer, useRef } from 'react';
import useSWR from 'swr';
import { CreateOrUpdateFormSubmissionCommand, FormsClient, FormSubmissionsClient, FormSubmissionSubmittedBy, OptionsSource, PatientsClient } from '../../../api/web-api-client';
import { AzureService } from '../../../services/azure';
import clientService from '../../../services/clientService';
import IComponent, { ISubmissionJsonField, ILayoutRow } from '../../../services/forms.interface';
import Keys from '../../../services/keys';
import { IData, IState, NetworkField } from '../shared/interfaces';
import moment from 'moment';
import { IFormViewError } from '../view/interfaces';
import { Engine } from 'json-rules-engine';
import { ADDRESS, ALLERGIES, ALLERGIESPMS, AUTOCOMPLETE, HIPAA, INSURANCE, MEDICATIONS, PROBLEMS, PROBLEMSPMS, SIGNATURE, SWITCHINPUT } from '../shared/constants';
import { usePatientAppContext } from '../../../context/patient-app-context';
import { applySubmissionJsonToComponents, preFillFormWithPatientData, regex, removeSignatureRowsFromLayout } from './form-view-utils';
import { ActionKind, emptyValidationMessages, initialState, reducer } from './form-view-reducer';
import { usePdfEffects } from './use-pdf-effects';
import { usePmsSystemEffects } from './use-pmssystem-effects';
import { usePayerEffects } from './use-payer-effects';
import { createPmsListComponent } from '../shared/helpers';

export const SUBMISSION_PMS_FIELDS_DATA = 'submission-pms-fields-data';

const FormViewContext = React.createContext<{
    state: IState;
    dispatch: React.Dispatch<any>;
    mutate: () => void;
    handleSubmit: () => void;
    handleChange: (event, id, value, parentId?) => void;
    handleCancel: () => void;

}>({
    state: initialState,
    dispatch: () => null,
    mutate: () => null,
    handleSubmit: () => null,
    handleChange: (event, id, value, parentId?) => null,
    handleCancel: () => null
});

export const useFormViewContext = () => React.useContext(FormViewContext);

export const FormViewContextProvider: React.FC = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { data, error, mutate } = useSWR(state.id ? [Keys.AzureForm, state.id, state.assignmentId] : null, (url, formId, legacy) => fetcher(state.id, state.legacy));
    const { data: submissionJson, mutate: mutateSubmissionJson } = useSWR([Keys.ApiFormSubmissionJson, state.submissionId], (url, id) => submissionJsonFetcher(state.submissionId));
    const { data: submissionByFormIdJson } = useSWR([Keys.ApiFormSubmissionFromFormIdJson, state.id], (url, templateType) => submissionJsonByFormIdFetcher(state.id));
    const canvasRefs = useRef([]);
    const patientsClient = clientService.getFormsClient(PatientsClient, null);
    const formSubmissionsClient = clientService.getFormsClient(FormSubmissionsClient, null);
    const { selectedPatient } = usePatientAppContext();
    const [submissionJsonReady, setSubmissionJsonReady] = React.useState(false);

    usePdfEffects(state, dispatch, canvasRefs);
    usePmsSystemEffects(state, dispatch);
    usePayerEffects(state, dispatch, state.locationId);

    const fetcher = async (formId: string, legacy: boolean): Promise<IData> => {

        if (!formId)
            throw new Error("Form Id is Required.");

        let form;
        let uri: string = null;
       
        if (state.submittedBy === FormSubmissionSubmittedBy.Patient) {
            form = await patientsClient.getFormWithUri(formId, legacy);
            uri = form.azureBlobSasUri;
        } else {
            const formsClient = clientService.getFormsClient(FormsClient, null);
            form = await formsClient.getFormWithUri(formId, legacy);
            uri =  form.azureBlobSasUri;
        }
        
        let newJson = {};
        
        if (uri) {
            const service = new AzureService();
            const jsonString = await service.getJsonFromAzure(uri);
           
            if (jsonString && jsonString !== '') {
                newJson = JSON.parse(jsonString);
            }
        }

        return { json: newJson, form: form };
    }

    const submissionJsonFetcher = async (id: string) => {

        if (!id)
            return;
       
        if (state.submittedBy === FormSubmissionSubmittedBy.Patient) {
            const query = await patientsClient.getFormSubmissionJson(id);
            return query;
        } else {
            const query = await formSubmissionsClient.getFormSubmissionJson(id);
            return query;
        }
    };

    const submissionJsonByFormIdFetcher = async (formId: string) => {

        if (!formId || state.submittedBy !== FormSubmissionSubmittedBy.Patient)
            return;

        return await patientsClient.getFormSubmissionJsonByFormId(formId);
    };

    const EnbableDisableChildComponents = (newComponents, row) => {
        row.children.map((column) => {
            column.children.map((layoutComponent) => {
                const component = newComponents[layoutComponent.id];
                component.disabled = !row.visible;
                if (component.childComponents) {
                    Object.keys(component.childComponents).map((childComponent) => {
                        const child = component.childComponents[childComponent];
                        child.disabled = !row.visible;
                    });
                }
            });
        });
    }

    const ShowHideRangeOfRows = (newLayout, newComponents, messages, overrideVisibility, visibility = null) => {

        if (messages?.length !== 2)
            return;

        const startInsurance = newLayout.findIndex((obj) => { return obj.id === messages[0]; });
        const endInsurance = newLayout.findIndex((obj) => { return obj.id === messages[1]; });

        // If either startInsurance or endInsurance is not found, return the original layout
        if (startInsurance === -1 || endInsurance === -1)
            return;

        newLayout.map((row, index) => {
            if (index >= startInsurance && index <= endInsurance) {

                if (row.visible === undefined)
                    row.visible = true;

                row.visible = overrideVisibility ? visibility : !row.visible;

                EnbableDisableChildComponents(newComponents, row);
            }
        });

    }


    const handleChange = (event, id: string, value: string, parentId?: string) => {
        
        if (state.preview && state.practiceView !== true)
            return;

        const newComponents = { ...state.data.json.components };
        const component = newComponents[id] || newComponents[parentId].childComponents[id];

        if (!component)
            return;

        const rule = state.data.json.rules?.find((obj) => { return obj.name === component.id; });

        if (rule) {

            const facts = { value: Array.isArray(value) ? component.value[0] : value };
            const engine = new Engine();
            engine.addRule(rule);
            engine
                .run(facts)
                .then(({ events, }) => {
                    events.map(event => {

                        const newLayout = [...state.data.json.layout];

                        switch (event.type) {
                            case 'ShowHide':
                                ShowHideRangeOfRows(newLayout, newComponents, event.params.message, false);
                                break;
                            case 'ShowHideInsurance':
                                ShowHideRangeOfRows(newLayout, newComponents, event.params.message, true, facts.value === '1');
                                break;
                            case 'SetValue':
                                event.params.message.map((message) => {
                                    if (newComponents[message]) {
                                        if (newComponents[message].type === SWITCHINPUT) {
                                            newComponents[message].value = facts.value === 'true' ? 'false' : '';
                                            delete newComponents[message].error
                                        }
                                    }
                                });
                                break;
                            case 'TypeOfPlan':
                                if (event.params.message?.length === 3) {
                                    const insuranceCompanyNameRow = newLayout[newLayout.findIndex((obj) => { return obj.id === event.params.message[0] })];
                                    const subscriberIdRow = newLayout[newLayout.findIndex((obj) => { return obj.id === event.params.message[1] })];
                                    const groupNumberRow = newLayout[newLayout.findIndex((obj) => { return obj.id === event.params.message[2] })];
                                    const subscriberIdLabel = newComponents[subscriberIdRow?.children?.[0]?.children?.[0]?.id];
                                    const subscriberIdComponent = newComponents[subscriberIdRow?.children[0]?.children[1]?.id];
                                    if (subscriberIdLabel && subscriberIdComponent) {
                                        const isNotMedicaid = (facts.value === '0' || facts.value === '2');
                                        insuranceCompanyNameRow.visible = isNotMedicaid;
                                        EnbableDisableChildComponents(newComponents, insuranceCompanyNameRow);
                                        groupNumberRow.visible = isNotMedicaid;
                                        EnbableDisableChildComponents(newComponents, groupNumberRow);
                                        subscriberIdLabel.content = isNotMedicaid ? 'Subscriber ID' : 'Medicaid ID';
                                        subscriberIdComponent.label = isNotMedicaid ? 'Subscriber ID' : 'Medicaid ID';
                                    }
                                }
                                break;
                        }

                        dispatch({ type: ActionKind.SET_LAYOUT, payload: newLayout });
                    });
                });
        }

        component.value = value || '';
        component.dirty = true;

        if (state.submittedBy === FormSubmissionSubmittedBy.Patient)
            validate(component);
        
        dispatch({ type: ActionKind.SET_COMPONENTS, payload: newComponents });

    }

    const handleSubmit = () => {

        dispatch({ type: ActionKind.SET_SUBMITTING, payload: true });

        const newComponents = { ...state.data.json.components };

        clearValuesOfHiddenComponents(newComponents);

        if (state.submittedBy === FormSubmissionSubmittedBy.Patient) {
            Object.keys(newComponents).forEach(key => {
                validate(newComponents[key], true);
                validateSwitchInputDescription(newComponents[key]);
            });
        }
        
        const hasErrors = Object.keys(newComponents).some((key) => {
            const component = newComponents[key];
            return (!component.disabled && component.error) || (component.childComponents && Object.keys(component.childComponents).some((childKey) => {
                const childComponent = component.childComponents[childKey];
                return !childComponent.disabled && childComponent.error;
            }));
        });

        if (!hasErrors) {
            commitSubmittion(state.data.json.components);
        } else {
            dispatch({ type: ActionKind.SET_COMPONENTS, payload: newComponents });
            formatValidationMessages(newComponents);
            dispatch({ type: ActionKind.SET_SUBMITTING, payload: false });
            scrollToFirstError(state.tempLayout, newComponents);
        }

    }

    const clearValuesOfHiddenComponents = (components) => {
        state.data.json.layout?.map((row, index) => {
            if (row.visible !== undefined && !row.visible) {
                row.children.map((column) => {
                    column.children.map((layoutComponent) => {
                        const component = components[layoutComponent.id];
                        component.value = '';
                        if (component.childComponents) {
                            Object.keys(component.childComponents).map((childComponent) => {
                                const child = component.childComponents[childComponent];
                                child.value = '';
                            });
                        }
                    });
                });
            }
        });
    }

    const commitSubmittion = async (components) => {
        try {

            // If no components are found, the form is probably blank, redirect to the forms index page
            if (!components) {
                dispatch({ type: ActionKind.SET_REDIRECT, payload: true });
                return;
            }  

            if (!state.assignmentId)
                throw new Error("Assignment Id is Required.");

            const responseArray = [] as ISubmissionJsonField[];

            const networkFields: Record<string, NetworkField> = {};

            Object.keys(components).forEach(key => {
                const component = components[key];
                if (component) {
                    if (component.value && component.type !== ADDRESS)
                        responseArray.push({ value: component.value, fieldId: component.fieldId, id: component.id, ...(component.externalId && { externalId: component.externalId }) });

                    if (component.childComponents) {
                        Object.keys(component.childComponents).forEach(childKey => {
                            const childComponent = component.childComponents[childKey];
                            if (childComponent) {
                                if (childComponent.value) {
                                    responseArray.push({ value: childComponent.value, fieldId: childComponent.fieldId, id: childComponent.id, ...(childComponent.externalId && { externalId: childComponent.externalId }) });
                                }
                                if (component.type === ALLERGIESPMS || component.type === PROBLEMSPMS) {
                                    networkFields[childComponent.id] = { label: childComponent.label, type: component.type, value: childComponent.value };
                                }
                            }
                        });
                    }
                }
            });

            const pmsFieldsbject = {
                id: SUBMISSION_PMS_FIELDS_DATA,
                value: networkFields
            } as ISubmissionJsonField;

            responseArray.push(pmsFieldsbject)

            let requiresPracticeSignature = false;
            if (state.data?.json?.components) {
                requiresPracticeSignature = Object.values(state.data?.json?.components as Map<string, IComponent>).some(value => value.type === SIGNATURE && value.practice === true);
            }

            const cmd = new CreateOrUpdateFormSubmissionCommand();
            cmd.formId = state.id || state.data.form.id;
            cmd.formAssignmentId = state.assignmentId;
            cmd.formSubmissionJson = JSON.stringify(responseArray);
            cmd.requiresPracticeSignature = requiresPracticeSignature;
            cmd.submittedBy = state.submittedBy;

            if (state.submittedBy === FormSubmissionSubmittedBy.Patient) {
                await patientsClient.createOrUpdate(cmd);
            } else {
                await formSubmissionsClient.createOrUpdate(cmd);
            }

            mutateSubmissionJson();

            dispatch({ type: ActionKind.SET_REDIRECT, payload: true });
        } catch (e) {
            dispatch({ type: ActionKind.SET_ERROR, payload: e });
        } finally {
            dispatch({ type: ActionKind.SET_SUBMITTING, payload: false });
        }
    }

    const handleCancel = () => {
        dispatch({ type: ActionKind.SET_OPEN_CONFIRM_CANCEL_DIALOGUE, payload: true });
    }

    const toMMDDYYYY = (value: any): string => {
        const date = toValidDate(value);
        return date ? moment(date).format('MM/DD/yyyy') : '';
    }

    const toValidDate = (value: any): Date | null => {
        if (!value) return null;
        if (value instanceof Date && !isNaN(value.getTime())) return value;
        // Try ISO string or MM/DD/yyyy
        const m = moment(value, moment.ISO_8601, true);
        if (m.isValid()) return m.toDate();
        // Try MM/DD/yyyy
        const m2 = moment(value, 'MM/DD/yyyy', true);
        if (m2.isValid()) return m2.toDate();
        return null;
    }

    const validateComponent = (component: IComponent, submit?: boolean) => {
        if (component.disabled) return;

        // required
        let error = component.required
            && (submit || component.dirty)
            && component.type !== ADDRESS
            && component.type !== ALLERGIES
            && component.type !== PROBLEMSPMS
            && component.type !== ALLERGIESPMS
            && component.type !== PROBLEMS
            && component.type !== MEDICATIONS
            && component.type !== HIPAA
            && component.type !== INSURANCE
            && (!component.value || component.value.length === 0);

        
        if (error) {
            component.error = { required: true, message: 'Please complete all required fields.' } as IFormViewError;
            return;
        }

        // HIPAA type check
        if (component.type === HIPAA && component.required && component.value !== 'true') {
            component.error = { required: true, message: 'HIPAA consent is required.' } as IFormViewError;
            error = true;
        }

        // invalid date
        if (component.type === 'DatePicker' && component.value) {
            const date = moment(toMMDDYYYY(component.value), 'MM/DD/yyyy', true);
            const minDate = '01/01/1900';
            const maxDate = '01/01/2050';

            if (!date.isValid() ||
                moment(date, 'MM/DD/yyyy').isBefore(moment(minDate, 'MM/DD/yyyy')) ||
                moment(date, 'MM/DD/yyyy').isAfter(moment(maxDate, 'MM/DD/yyyy'))) {
                component.error = { required: false, message: 'Please enter a valid date with a 4 digit year.' } as IFormViewError;
                error = true;
            }
        }

        // clear error and check regex
        if (!error) {
            delete component.error;
            validateRegex(component);
        }
    };

    const validateChildComponents = (component: IComponent, submit?: boolean) => {

        if (component.error) return;
        
        if (component.childComponents) {
            Object.keys(component.childComponents).forEach(key => {
                validate(component.childComponents[key], submit);
            });
            // if none of the child components have errors, clear the parent component error
            const hasChildErrors = Object.keys(component.childComponents).some(key => {
                return component.childComponents[key].error;
            });
            if (!hasChildErrors) {
                delete component.error;
            }
        }
    };

    const validate = (component: IComponent, submit?: boolean) => {
        validateComponent(component, submit);
        validateChildComponents(component, submit);
    };

    const validateSwitchInputDescription = (component: IComponent) => {
        if (component.disabled) return;

        
        // explain text is required if 'yes' button is selected in switchInput
        if (component.type === SWITCHINPUT && component.value === 'true' && component.explaination && component.required && component.childComponents && Object.values(component.childComponents)[0].value.length === 0) {
            
            const childKey = Object.keys(component.childComponents)[0];
            const childComponent = component.childComponents[childKey];
            childComponent.error = { required: true, message: 'Please complete all required fields.' } as IFormViewError;
            return childComponent.error;
        }
        return;
    };

    const validateRegex = (component: IComponent) => {

        if (component.mask && component.value?.length > 0) {
            const maskRegExp = regex(component.mask);

            let valueToValidate = component.value;

            // Ensure value is a string before trimming
            if (typeof valueToValidate === 'string') {
                valueToValidate = valueToValidate.trim();
            }

            const match = valueToValidate.match(maskRegExp);
            if (!match) {
                component.error = { required: false, message: 'Please enter a valid ' + component.label + '.' } as IFormViewError;
            } else {
                delete component.error;
            }
            if (component.childComponents) {
                Object.keys(component.childComponents).forEach(key => {
                    validateRegex(component);
                });
            }
        }
    }

    const scrollToFirstError = (layout: ILayoutRow[] | undefined, components: Record<string, IComponent>) => {
        if (!layout) return;

        setTimeout(() => {
            for (const row of layout) {
                if (row.visible === false) continue;

                const hasErrorInRow = row.children?.some(column =>
                    //check component or child components have errors
                    column.children?.some(layoutComponent => {
                        const component = components[layoutComponent.id];
                        if (!component) return false;

                        if (!component.disabled && component.error) return true;

                        if (component.childComponents) {
                            return Object.values(component.childComponents).some(
                                (child: IComponent) => !child.disabled && child.error
                            );
                        }

                        return false;
                    })
                );

                if (hasErrorInRow) {
                    const element = document.getElementById(row.id);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                    return; // Stop after first error found
                }
            }
        }, 0);
    };

    const formatValidationMessages = (newComponents) => {

        const errors = [];
        Object.keys(newComponents).forEach(key => {
            const component = newComponents[key];
            if (component) {
                if (component.error)
                    errors.push(component.error);

                if (component.childComponents) {
                    Object.keys(component.childComponents).forEach(key => {
                        const childComponent = component.childComponents[key];
                        if (childComponent) {
                            if (childComponent.error)
                                errors.push(childComponent.error);
                        }
                    });
                }
            }

        });

        const messages = { ...state.validationMessages };
        const errorValues = Object.values(errors);
        if (errorValues.some((err: IFormViewError) => { return err.required == true; })) {
            messages['required'] = ['Please complete all required fields.'];
        } else {
            messages['required'] = [];
        }
        const regexErrors = errorValues.filter((err: IFormViewError) => { return err.required == false });
        messages['regex'] = regexErrors.map((err: IFormViewError) => err.message);
        dispatch({ type: ActionKind.SET_VALIDATION_MESSAGES, payload: messages });

    }

    // Track if patient data prefill has run
    const patientPrefillInitialized = useRef(false);

    // Initial data load: prefill patient data, remove signature rows
    useEffect(() => {
        if (patientPrefillInitialized.current || !data || state.data)
            return;

        // Only run once when data is first loaded
        data.json.components = preFillFormWithPatientData(data.json.components, selectedPatient);
        data.json.layout = removeSignatureRowsFromLayout(data.json.layout, data.json.components, state.submittedBy);
        dispatch({ type: ActionKind.SET_DATA, payload: data });
        dispatch({ type: ActionKind.SET_ERROR, payload: error });
        dispatch({ type: ActionKind.SET_LOADING, payload: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, state.submittedBy, error]);

    // Validation messages: Clear after timeout
    useEffect(() => {
        setTimeout(() => {
            dispatch({ type: ActionKind.SET_VALIDATION_MESSAGES, payload: emptyValidationMessages });

        }, 600000);
    }, [state.validationMessages]);

    const submissionJsonLoadedRef = useRef(false);

    useEffect(() => {
        if (!state.submissionId) return; // Only run if loading an existing submission
  
        if (!submissionJsonLoadedRef.current && submissionJson !== undefined && state.data) {

            if (submissionJson != null) {

                state.data.json.components = applySubmissionJsonToComponents(
                    state.data.json.components,
                    JSON.parse(submissionJson)
                );
                dispatch({ type: ActionKind.SET_DATA, payload: { ...state.data } });
            }
            submissionJsonLoadedRef.current = true;
            setSubmissionJsonReady(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionJson, state.data?.json?.components, state.pmsSystemLoaded, state.submissionId]);

    useEffect(() => {
        if (state.submissionId) return; // Only run if NOT loading an existing submission

        if (!submissionJsonLoadedRef.current && state.data) {
            // No submissionJson to load, so mark as ready
            submissionJsonLoadedRef.current = true;
            setSubmissionJsonReady(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.data?.json?.components, state.pmsSystemLoaded, state.submissionId]);

    const submissionByTemplateTypeJsonLoadedRef = useRef(false);

    // Submission By Template Type JSON: Apply to components/layout
    useEffect(() => {
        if (
            !state.pmsSystemLoaded ||
            submissionByTemplateTypeJsonLoadedRef.current ||
            !submissionByFormIdJson ||
            !state.data?.json?.components ||
            !submissionJsonReady // Only run after submissionJson is ready (applied or skipped)
        ) return;
  
        state.data.json.components = applySubmissionJsonToComponents(
            state.data.json.components,
            JSON.parse(submissionByFormIdJson)
        );
        dispatch({ type: ActionKind.SET_DATA, payload: { ...state.data } });
        submissionByTemplateTypeJsonLoadedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionByFormIdJson, state.data?.json?.components, submissionJsonReady, state.pmsSystemLoaded]);

    // Layout: Filter rows for practice view
    useEffect(() => {
        if (!state.data?.json?.layout)
            return;

        const newLayout = state.data.json.layout.filter(row => {
            // If row is for practice and not in practice view, exclude

            if (row.practice === true && !state.practiceView) return false;

            // If all components in the row are practice and not in practice view, exclude
            if (
                Array.isArray(row.children) &&
                row.children.every(col =>
                    Array.isArray(col.children) &&
                    col.children.every(comp => {
                        // comp.id is the component id, get the component from state.data.json.components
                        const component = state.data.json.components[comp.id];
                        return component && component.practice === true;
                    })
                ) &&
                !state.practiceView
            ) {
                return false;
            }

            // Otherwise, include if not explicitly hidden
            return row.visible !== false;
        });

        dispatch({ type: ActionKind.SET_TEMP_LAYOUT, payload: newLayout });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.data?.json?.layout]);



    return (
        <FormViewContext.Provider value={{
            state,
            dispatch,
            mutate,
            handleSubmit,
            handleChange,
            handleCancel
        }} >
            {children}
            {state.pagePlacesHolders && state.pagePlacesHolders.map((page, index) => (
                <canvas
                    key={`child-${index}`}
                    ref={(ref) => canvasRefs.current.push(ref)}
                    style={{ display: 'none' }}
                >
                </canvas>
            ))}
        </FormViewContext.Provider>
    );
};


